import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../lib/auth";

function formatDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleString("en-AU", {
    timeZone: "Australia/Sydney",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
}

function formatTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleTimeString("en-AU", {
    timeZone: "Australia/Sydney",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
}

function eventLabel(event) {
  const labels = {
    panic_triggered: "PANIC TRIGGERED",
    control_room_acknowledged: "ACKNOWLEDGED",
    response_commenced: "RESPONSE COMMENCED",
    emergency_resolved: "RESOLVED",
  };

  return labels[event] || String(event || "EVENT").replaceAll("_", " ").toUpperCase();
}

function msBetween(start, end) {
  if (!start || !end) return null;

  const a = new Date(start).getTime();
  const b = new Date(end).getTime();

  if (!Number.isFinite(a) || !Number.isFinite(b) || b < a) return null;

  return b - a;
}

function formatDuration(ms) {
  if (ms == null || !Number.isFinite(ms)) return "—";

  const totalSeconds = Math.max(0, Math.round(ms / 1000));

  if (totalSeconds < 60) return `${totalSeconds}s`;

  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  if (minutes < 60) {
    return seconds ? `${minutes}m ${seconds}s` : `${minutes}m`;
  }

  const hours = Math.floor(minutes / 60);
  const remMinutes = minutes % 60;

  return remMinutes ? `${hours}h ${remMinutes}m` : `${hours}h`;
}

function average(values) {
  const valid = values.filter((value) => Number.isFinite(value));
  if (!valid.length) return null;

  return valid.reduce((sum, value) => sum + value, 0) / valid.length;
}

function csvEscape(value) {
  const text = value == null ? "" : String(value);
  return `"${text.replaceAll('"', '""')}"`;
}

// V4.0E-2J — KPI integrity thresholds.
// These are intentionally conservative operational sanity bounds,
// not SLA targets. Cases outside them remain reviewable but do not
// distort aggregate performance statistics.
const KPI_LIMITS = {
  acknowledgementMs: 60 * 60 * 1000,      // 1 hour
  responseStartMs: 4 * 60 * 60 * 1000,    // 4 hours
  resolutionMs: 24 * 60 * 60 * 1000,      // 24 hours
};


const EMERGENCY_SLA = {
  acknowledgementMs: 2 * 60 * 1000,
  responseMs: 5 * 60 * 1000,
  supervisorEscalationMs: 10 * 60 * 1000,
};

function buildSlaCompliance(item) {
  const acknowledgementMs = msBetween(
    item.created_at,
    item.acknowledged_at
  );

  const responseMs = msBetween(
    item.created_at,
    item.responding_at
  );

  const acknowledgementKnown = acknowledgementMs != null;
  const responseKnown = responseMs != null;

  const acknowledgementBreached =
    acknowledgementKnown &&
    acknowledgementMs > EMERGENCY_SLA.acknowledgementMs;

  const responseBreached =
    responseKnown &&
    responseMs > EMERGENCY_SLA.responseMs;

  return {
    acknowledgementMs,
    responseMs,
    acknowledgementKnown,
    responseKnown,
    acknowledgementBreached,
    responseBreached,
    reviewRequired:
      !acknowledgementKnown || !responseKnown,
    compliant:
      acknowledgementKnown &&
      responseKnown &&
      !acknowledgementBreached &&
      !responseBreached,
  };
}

function buildCaseTimingIntegrity(item) {
  const acknowledgementMs = msBetween(
    item.created_at,
    item.acknowledged_at
  );

  const responseStartMs = msBetween(
    item.acknowledged_at,
    item.responding_at
  );

  const resolutionMs = msBetween(
    item.created_at,
    item.resolved_at
  );

  const issues = [];

  if (acknowledgementMs == null) {
    issues.push("Missing or invalid acknowledgement timing");
  } else if (acknowledgementMs > KPI_LIMITS.acknowledgementMs) {
    issues.push("Acknowledgement timing outside KPI integrity range");
  }

  if (responseStartMs == null) {
    issues.push("Missing or invalid response commencement timing");
  } else if (responseStartMs > KPI_LIMITS.responseStartMs) {
    issues.push("Response commencement timing outside KPI integrity range");
  }

  if (resolutionMs == null) {
    issues.push("Missing or invalid resolution timing");
  } else if (resolutionMs > KPI_LIMITS.resolutionMs) {
    issues.push("Resolution timing outside KPI integrity range");
  }

  return {
    acknowledgementMs,
    responseStartMs,
    resolutionMs,
    validAcknowledgement:
      acknowledgementMs != null &&
      acknowledgementMs <= KPI_LIMITS.acknowledgementMs,
    validResponseStart:
      responseStartMs != null &&
      responseStartMs <= KPI_LIMITS.responseStartMs,
    validResolution:
      resolutionMs != null &&
      resolutionMs <= KPI_LIMITS.resolutionMs,
    complete:
      issues.length === 0,
    issues,
  };
}

export default function EmergencyHistoryPanel() {
  const { api } = useAuth();

  const [cases, setCases] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [officerFilter, setOfficerFilter] = useState("all");
  const [siteFilter, setSiteFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const loadHistory = async () => {
    setLoading(true);
    setError("");

    try {
      const res = await api.get(
        "/panic/history?limit=200"
      );

      const nextCases = res?.data?.cases || [];
      setCases(nextCases);

      setSelectedId((current) => {
        if (
          current &&
          nextCases.some((item) => item.alert_id === current)
        ) {
          return current;
        }

        return nextCases[0]?.alert_id || null;
      });
    } catch (err) {
      console.error("Failed to load emergency history", err);
      setError(
        err?.response?.data?.detail ||
        "Failed to load emergency history"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadHistory();
  }, []);

  const officers = useMemo(
    () =>
      [...new Set(
        cases
          .map((item) => item.officer_name)
          .filter(Boolean)
      )].sort(),
    [cases]
  );

  const sites = useMemo(
    () =>
      [...new Set(
        cases
          .map((item) => item.site_name)
          .filter(Boolean)
      )].sort(),
    [cases]
  );

  const filteredCases = useMemo(() => {
    const fromMs = dateFrom
      ? new Date(`${dateFrom}T00:00:00`).getTime()
      : null;

    const toMs = dateTo
      ? new Date(`${dateTo}T23:59:59.999`).getTime()
      : null;

    return cases.filter((item) => {
      if (
        officerFilter !== "all" &&
        item.officer_name !== officerFilter
      ) {
        return false;
      }

      if (
        siteFilter !== "all" &&
        item.site_name !== siteFilter
      ) {
        return false;
      }

      const eventMs = new Date(
        item.created_at || item.resolved_at || 0
      ).getTime();

      if (fromMs && (!Number.isFinite(eventMs) || eventMs < fromMs)) {
        return false;
      }

      if (toMs && (!Number.isFinite(eventMs) || eventMs > toMs)) {
        return false;
      }

      return true;
    });
  }, [cases, officerFilter, siteFilter, dateFrom, dateTo]);

  const enrichedCases = useMemo(
    () =>
      filteredCases.map((item) => ({
        ...item,
        timingIntegrity: buildCaseTimingIntegrity(item),
        slaCompliance: buildSlaCompliance(item),
      })),
    [filteredCases]
  );

  useEffect(() => {
    if (
      selectedId &&
      enrichedCases.some((item) => item.alert_id === selectedId)
    ) {
      return;
    }

    setSelectedId(enrichedCases[0]?.alert_id || null);
  }, [enrichedCases, selectedId]);

  const selectedCase = useMemo(
    () =>
      enrichedCases.find((item) => item.alert_id === selectedId) ||
      null,
    [enrichedCases, selectedId]
  );

  const metrics = useMemo(() => {
    const acknowledgementTimes = enrichedCases
      .filter((item) => item.timingIntegrity.validAcknowledgement)
      .map((item) => item.timingIntegrity.acknowledgementMs);

    const responseCommencementTimes = enrichedCases
      .filter((item) => item.timingIntegrity.validResponseStart)
      .map((item) => item.timingIntegrity.responseStartMs);

    const resolutionTimes = enrichedCases
      .filter((item) => item.timingIntegrity.validResolution)
      .map((item) => item.timingIntegrity.resolutionMs);

    const incompleteCount = enrichedCases.filter(
      (item) => !item.timingIntegrity.complete
    ).length;

    const slaEligible = enrichedCases.filter(
      (item) => !item.slaCompliance.reviewRequired
    );

    const slaCompliantCount = slaEligible.filter(
      (item) => item.slaCompliance.compliant
    ).length;

    const slaBreachCount = slaEligible.filter(
      (item) => !item.slaCompliance.compliant
    ).length;

    const slaCompliancePercent = slaEligible.length
      ? (slaCompliantCount / slaEligible.length) * 100
      : null;

    return {
      total: enrichedCases.length,
      avgAcknowledgement: average(acknowledgementTimes),
      avgResponseCommencement: average(responseCommencementTimes),
      avgResolution: average(resolutionTimes),
      longestResolution:
        resolutionTimes.length
          ? Math.max(...resolutionTimes)
          : null,
      acknowledgementSample: acknowledgementTimes.length,
      responseSample: responseCommencementTimes.length,
      resolutionSample: resolutionTimes.length,
      incompleteCount,
      slaEligibleCount: slaEligible.length,
      slaCompliantCount,
      slaBreachCount,
      slaCompliancePercent,
    };
  }, [enrichedCases]);

  const exportCsv = () => {
    if (!enrichedCases.length) return;

    const headers = [
      "Case ID",
      "Officer",
      "Site",
      "Triggered",
      "Acknowledged",
      "Response Commenced",
      "Resolved",
      "Acknowledged By",
      "Responding By",
      "Resolved By",
      "Acknowledgement Seconds",
      "Response Commencement Seconds",
      "Resolution Seconds",
      "KPI Integrity",
      "KPI Integrity Issues",
      "SLA Status",
      "Acknowledgement SLA Breach",
      "Response SLA Breach",
      "Resolution Note",
    ];

    const rows = enrichedCases.map((item) => {
      const integrity = item.timingIntegrity;

      return [
        item.alert_id,
        item.officer_name,
        item.site_name,
        item.created_at,
        item.acknowledged_at,
        item.responding_at,
        item.resolved_at,
        item.acknowledged_by_name,
        item.responding_by_name,
        item.resolved_by_name,
        integrity.acknowledgementMs == null
          ? ""
          : Math.round(integrity.acknowledgementMs / 1000),
        integrity.responseStartMs == null
          ? ""
          : Math.round(integrity.responseStartMs / 1000),
        integrity.resolutionMs == null
          ? ""
          : Math.round(integrity.resolutionMs / 1000),
        integrity.complete ? "Included in KPIs" : "Incomplete Timing Data",
        integrity.issues.join("; "),
        item.slaCompliance.reviewRequired
          ? "Timing Review Required"
          : item.slaCompliance.compliant
            ? "SLA Met"
            : "SLA Breach",
        item.slaCompliance.acknowledgementBreached ? "Yes" : "No",
        item.slaCompliance.responseBreached ? "Yes" : "No",
        item.resolution_note,
      ];
    });

    const csv = [
      headers.map(csvEscape).join(","),
      ...rows.map((row) => row.map(csvEscape).join(",")),
    ].join("\r\n");

    const blob = new Blob([csv], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = `rapidx-emergency-case-intelligence-${new Date()
      .toISOString()
      .slice(0, 10)}.csv`;

    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const clearFilters = () => {
    setOfficerFilter("all");
    setSiteFilter("all");
    setDateFrom("");
    setDateTo("");
  };

  return (
    <section className="mb-6 rounded-2xl border border-zinc-700 bg-[#111113] overflow-hidden">
      <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 px-5 py-4 border-b border-zinc-800">
        <div>
          <div className="text-xs font-black uppercase tracking-[0.2em] text-red-300">
            V4.0E-2J
          </div>

          <h2 className="text-xl font-black text-white mt-1">
            Emergency SLA & Escalation Intelligence
          </h2>

          <p className="text-sm text-zinc-400 mt-1">
            Management KPIs exclude incomplete or anomalous legacy timings while all emergency cases remain reviewable.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={exportCsv}
            disabled={!enrichedCases.length}
            className="px-4 py-2 rounded-lg border border-emerald-500/40 bg-emerald-950/30 text-emerald-200 font-bold hover:bg-emerald-900/40 disabled:opacity-40"
          >
            Export CSV
          </button>

          <button
            onClick={loadHistory}
            disabled={loading}
            className="px-4 py-2 rounded-lg border border-zinc-600 bg-zinc-900 text-white font-bold hover:bg-zinc-800 disabled:opacity-50"
          >
            {loading ? "Refreshing..." : "Refresh History"}
          </button>
        </div>
      </div>

      {error && (
        <div className="m-4 rounded-lg border border-red-500/40 bg-red-950/40 px-4 py-3 text-red-200">
          {error}
        </div>
      )}

      {!error && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-8 gap-3 p-4 border-b border-zinc-800 bg-black/20">
            <div className="rounded-xl border border-zinc-800 bg-black/40 p-4">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500">
                Resolved Cases
              </div>
              <div className="text-2xl font-black text-white mt-1">
                {metrics.total}
              </div>
            </div>

            <div className="rounded-xl border border-zinc-800 bg-black/40 p-4">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500">
                Avg Acknowledge
              </div>
              <div className="text-2xl font-black text-emerald-300 mt-1">
                {formatDuration(metrics.avgAcknowledgement)}
              </div>
              <div className="text-[10px] text-zinc-600 mt-1">
                n={metrics.acknowledgementSample}
              </div>
            </div>

            <div className="rounded-xl border border-zinc-800 bg-black/40 p-4">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500">
                Avg Response Start
              </div>
              <div className="text-2xl font-black text-amber-300 mt-1">
                {formatDuration(metrics.avgResponseCommencement)}
              </div>
              <div className="text-[10px] text-zinc-600 mt-1">
                n={metrics.responseSample}
              </div>
            </div>

            <div className="rounded-xl border border-zinc-800 bg-black/40 p-4">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500">
                Avg Resolution
              </div>
              <div className="text-2xl font-black text-sky-300 mt-1">
                {formatDuration(metrics.avgResolution)}
              </div>
              <div className="text-[10px] text-zinc-600 mt-1">
                n={metrics.resolutionSample}
              </div>
            </div>

            <div className="rounded-xl border border-zinc-800 bg-black/40 p-4">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500">
                Longest Resolution
              </div>
              <div className="text-2xl font-black text-red-300 mt-1">
                {formatDuration(metrics.longestResolution)}
              </div>
            </div>

            <div className="rounded-xl border border-amber-500/30 bg-amber-950/20 p-4">
              <div className="text-[10px] uppercase tracking-wider text-amber-300">
                Incomplete Timing Data
              </div>
              <div className="text-2xl font-black text-amber-200 mt-1">
                {metrics.incompleteCount}
              </div>
              <div className="text-[10px] text-zinc-500 mt-1">
                retained for case review
              </div>
            </div>
            <div className="rounded-xl border border-emerald-500/30 bg-emerald-950/20 p-4">
              <div className="text-[10px] uppercase tracking-wider text-emerald-300">
                SLA Compliance
              </div>
              <div className="text-2xl font-black text-emerald-200 mt-1">
                {metrics.slaCompliancePercent == null
                  ? "—"
                  : `${metrics.slaCompliancePercent.toFixed(1)}%`}
              </div>
              <div className="text-[10px] text-zinc-500 mt-1">
                n={metrics.slaEligibleCount}
              </div>
            </div>

            <div className="rounded-xl border border-red-500/30 bg-red-950/20 p-4">
              <div className="text-[10px] uppercase tracking-wider text-red-300">
                SLA Breaches
              </div>
              <div className="text-2xl font-black text-red-200 mt-1">
                {metrics.slaBreachCount}
              </div>
              <div className="text-[10px] text-zinc-500 mt-1">
                acknowledge / response
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3 p-4 border-b border-zinc-800">
            <select
              value={officerFilter}
              onChange={(event) => setOfficerFilter(event.target.value)}
              className="rounded-lg border border-zinc-700 bg-black px-3 py-2 text-white"
            >
              <option value="all">All officers</option>
              {officers.map((officer) => (
                <option key={officer} value={officer}>
                  {officer}
                </option>
              ))}
            </select>

            <select
              value={siteFilter}
              onChange={(event) => setSiteFilter(event.target.value)}
              className="rounded-lg border border-zinc-700 bg-black px-3 py-2 text-white"
            >
              <option value="all">All sites</option>
              {sites.map((site) => (
                <option key={site} value={site}>
                  {site}
                </option>
              ))}
            </select>

            <input
              type="date"
              value={dateFrom}
              onChange={(event) => setDateFrom(event.target.value)}
              className="rounded-lg border border-zinc-700 bg-black px-3 py-2 text-white"
              aria-label="Emergency history from date"
            />

            <input
              type="date"
              value={dateTo}
              onChange={(event) => setDateTo(event.target.value)}
              className="rounded-lg border border-zinc-700 bg-black px-3 py-2 text-white"
              aria-label="Emergency history to date"
            />

            <button
              onClick={clearFilters}
              className="rounded-lg border border-zinc-700 bg-zinc-900 px-3 py-2 text-zinc-200 font-bold hover:bg-zinc-800"
            >
              Clear Filters
            </button>
          </div>
        </>
      )}

      {!error && !loading && enrichedCases.length === 0 && (
        <div className="px-5 py-10 text-center text-zinc-500">
          No resolved emergency cases match the selected filters.
        </div>
      )}

      {enrichedCases.length > 0 && (
        <div className="grid grid-cols-1 xl:grid-cols-[380px_1fr] min-h-[430px]">
          <div className="border-b xl:border-b-0 xl:border-r border-zinc-800 max-h-[560px] overflow-y-auto">
            {enrichedCases.map((item) => {
              const selected = item.alert_id === selectedId;
              const integrity = item.timingIntegrity;

              return (
                <button
                  key={item.alert_id}
                  onClick={() => setSelectedId(item.alert_id)}
                  className={`w-full text-left p-4 border-b border-zinc-800 transition ${
                    selected
                      ? "bg-red-950/30 border-l-4 border-l-red-500"
                      : "hover:bg-zinc-900/70 border-l-4 border-l-transparent"
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-black text-white">
                        {item.officer_name || "Officer"}
                      </div>

                      <div className="text-sm text-zinc-400 mt-1">
                        {item.site_name || "Unknown site"}
                      </div>
                    </div>

                    <div className="flex flex-col items-end gap-1">
                      <span className="rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2 py-1 text-[10px] font-black text-emerald-300">
                        RESOLVED
                      </span>

                      {!integrity.complete && (
                        <span className="rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-1 text-[10px] font-black text-amber-300">
                          INCOMPLETE TIMING DATA
                        </span>
                      )}

                      {!item.slaCompliance.reviewRequired && (
                        <span
                          className={`rounded-full border px-2 py-1 text-[10px] font-black ${
                            item.slaCompliance.compliant
                              ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
                              : "border-red-500/40 bg-red-500/10 text-red-300"
                          }`}
                        >
                          {item.slaCompliance.compliant
                            ? "SLA MET"
                            : "SLA BREACH"}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
                    <div className="text-zinc-500">
                      Ack{" "}
                      <span className="text-zinc-300 font-semibold">
                        {formatDuration(integrity.acknowledgementMs)}
                      </span>
                    </div>

                    <div className="text-zinc-500 text-right">
                      Total{" "}
                      <span className="text-zinc-300 font-semibold">
                        {formatDuration(integrity.resolutionMs)}
                      </span>
                    </div>
                  </div>

                  <div className="text-xs text-zinc-500 mt-2">
                    {formatDateTime(item.resolved_at || item.created_at)}
                  </div>

                  <div className="text-xs font-mono text-zinc-600 mt-1 truncate">
                    {item.alert_id}
                  </div>
                </button>
              );
            })}
          </div>

          <div className="p-5">
            {selectedCase && (
              <div className="space-y-5">
                {!selectedCase.timingIntegrity.complete && (
                  <div className="rounded-xl border border-amber-500/40 bg-amber-950/20 p-4">
                    <div className="text-xs font-black uppercase tracking-[0.18em] text-amber-300">
                      Incomplete Timing Data
                    </div>

                    <div className="text-sm text-zinc-300 mt-2">
                      This emergency remains fully available for case review, but one or more timing values are excluded from management KPI averages.
                    </div>

                    <ul className="mt-3 space-y-1 text-sm text-amber-100">
                      {selectedCase.timingIntegrity.issues.map((issue) => (
                        <li key={issue}>• {issue}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
                  <div className="rounded-xl bg-black/40 border border-zinc-800 p-3">
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500">
                      Officer
                    </div>
                    <div className="font-bold text-white mt-1">
                      {selectedCase.officer_name || "—"}
                    </div>
                  </div>

                  <div className="rounded-xl bg-black/40 border border-zinc-800 p-3">
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500">
                      Site
                    </div>
                    <div className="font-bold text-white mt-1">
                      {selectedCase.site_name || "—"}
                    </div>
                  </div>

                  <div className="rounded-xl bg-black/40 border border-zinc-800 p-3">
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500">
                      Triggered
                    </div>
                    <div className="font-bold text-white mt-1">
                      {formatDateTime(selectedCase.created_at)}
                    </div>
                  </div>

                  <div className="rounded-xl bg-black/40 border border-zinc-800 p-3">
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500">
                      Resolved
                    </div>
                    <div className="font-bold text-emerald-300 mt-1">
                      {formatDateTime(selectedCase.resolved_at)}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="rounded-xl border border-emerald-500/30 bg-emerald-950/20 p-4">
                    <div className="text-[10px] uppercase tracking-wider text-emerald-300">
                      Acknowledgement Time
                    </div>
                    <div className="text-xl font-black text-white mt-1">
                      {formatDuration(
                        selectedCase.timingIntegrity.acknowledgementMs
                      )}
                    </div>
                    <div className="text-[10px] text-zinc-600 mt-1">
                      {selectedCase.timingIntegrity.validAcknowledgement
                        ? "included in KPI"
                        : "excluded from KPI"}
                    </div>
                  </div>

                  <div className="rounded-xl border border-amber-500/30 bg-amber-950/20 p-4">
                    <div className="text-[10px] uppercase tracking-wider text-amber-300">
                      Response Commencement
                    </div>
                    <div className="text-xl font-black text-white mt-1">
                      {formatDuration(
                        selectedCase.timingIntegrity.responseStartMs
                      )}
                    </div>
                    <div className="text-[10px] text-zinc-600 mt-1">
                      {selectedCase.timingIntegrity.validResponseStart
                        ? "included in KPI"
                        : "excluded from KPI"}
                    </div>
                  </div>

                  <div className="rounded-xl border border-sky-500/30 bg-sky-950/20 p-4">
                    <div className="text-[10px] uppercase tracking-wider text-sky-300">
                      Total Resolution Time
                    </div>
                    <div className="text-xl font-black text-white mt-1">
                      {formatDuration(
                        selectedCase.timingIntegrity.resolutionMs
                      )}
                    </div>
                    <div className="text-[10px] text-zinc-600 mt-1">
                      {selectedCase.timingIntegrity.validResolution
                        ? "included in KPI"
                        : "excluded from KPI"}
                    </div>
                  </div>
                </div>

                <div className="rounded-xl border border-zinc-800 bg-black/30 p-4">
                  <div className="text-xs font-black uppercase tracking-[0.18em] text-zinc-400 mb-4">
                    Emergency Audit Trail
                  </div>

                  <div className="space-y-4">
                    {(selectedCase.workflow_history || []).map((item, index) => (
                      <div
                        key={`${item.event}-${item.timestamp || index}`}
                        className="grid grid-cols-[78px_1fr] gap-4"
                      >
                        <div className="font-mono text-sm text-zinc-500">
                          {formatTime(item.timestamp)}
                        </div>

                        <div className="border-l border-zinc-700 pl-4">
                          <div className="font-black text-white">
                            {eventLabel(item.event)}
                          </div>

                          {item.actor_name && (
                            <div className="text-sm text-zinc-300 mt-1">
                              {item.actor_name}
                            </div>
                          )}

                          {item.note && (
                            <div className="mt-2 rounded-lg bg-zinc-900 border border-zinc-800 p-3 text-sm text-zinc-300 whitespace-pre-wrap">
                              {item.note}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="rounded-xl border border-emerald-500/30 bg-emerald-950/20 p-4">
                  <div className="text-xs font-black uppercase tracking-[0.18em] text-emerald-300">
                    Resolution
                  </div>

                  <div className="mt-2 text-white font-semibold">
                    {selectedCase.resolution_note || "No resolution note recorded."}
                  </div>

                  <div className="mt-3 text-sm text-zinc-400">
                    Resolved by{" "}
                    <span className="text-zinc-200 font-semibold">
                      {selectedCase.resolved_by_name || "Control Room"}
                    </span>
                    {" · "}
                    {formatDateTime(selectedCase.resolved_at)}
                  </div>
                </div>

                <div className="text-xs text-zinc-600 font-mono break-all">
                  Case ID: {selectedCase.alert_id}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
